import { Entypo } from "@expo/vector-icons";
import React from "react";
import { TouchableOpacity, View } from "react-native";

interface PaginationProps {
  currentPage: number;
  totalPages: number;
  onPageChange: (page: number) => void;
}

const Pagination: React.FC<PaginationProps> = ({
  currentPage,
  totalPages,
  onPageChange,
}) => {
  const handlePrevious = () => {
    if (currentPage > 1) {
      onPageChange(currentPage - 1);
    }
  };

  const handleNext = () => {
    if (currentPage < totalPages) {
      onPageChange(currentPage + 1);
    }
  };

  // Generate page numbers to display
  const getPageNumbers = () => {
    const pages = [];
    const maxPagesToShow = 5;

    if (totalPages <= maxPagesToShow) {
      // Show all pages if total is less than or equal to maxPagesToShow
      for (let i = 1; i <= totalPages; i++) {
        pages.push(i);
      }
    } else {
      // Always show first page
      pages.push(1);

      // Calculate start and end of middle pages
      let startPage = Math.max(2, currentPage - 1);
      let endPage = Math.min(totalPages - 1, currentPage + 1);

      // Adjust if we're at the beginning
      if (currentPage <= 3) {
        endPage = 4;
      }

      // Adjust if we're at the end
      if (currentPage >= totalPages - 2) {
        startPage = totalPages - 3;
      }

      // Add ellipsis after first page if needed
      if (startPage > 2) {
        pages.push("...");
      }

      // Add middle pages
      for (let i = startPage; i <= endPage; i++) {
        pages.push(i);
      }

      // Add ellipsis before last page if needed
      if (endPage < totalPages - 1) {
        pages.push("...");
      }

      // Always show last page
      pages.push(totalPages);
    }

    return pages;
  };

  const pageNumbers = getPageNumbers();

  return (
    <View className="flex-row items-center justify-center py-4">
      {/* Previous button */}
      <TouchableOpacity
        onPress={handlePrevious}
        disabled={currentPage === 1}
        className={`px-3 py-2 rounded-lg mr-2 ${
          currentPage === 1 ? "opacity-50" : ""
        }`}
      >
        <Entypo name="chevron-left" size={20} color="#8F9BB3" />
      </TouchableOpacity>

      {/* Page numbers */}
      {pageNumbers.map((page, index) => {
        if (page === "...") {
          return (
            <View key={`ellipsis-${index}`} className="px-2">
              <div className="text-gray-500">...</div>
            </View>
          );
        }

        const isCurrentPage = page === currentPage;

        return (
          <TouchableOpacity
            key={`page-${page}`}
            onPress={() => onPageChange(page as number)}
            className={`min-w-[36px] h-[36px] rounded-full items-center justify-center mx-1 ${
              isCurrentPage ? "bg-blue-500" : "bg-gray-100"
            }`}
          >
            <div
              className={
                isCurrentPage ? "text-white font-bold" : "text-gray-700"
              }
            >
              {page}
            </div>
          </TouchableOpacity>
        );
      })}

      {/* Next button */}
      <TouchableOpacity
        onPress={handleNext}
        disabled={currentPage === totalPages}
        className={`px-3 py-2 rounded-lg ml-2 ${
          currentPage === totalPages ? "opacity-50" : ""
        }`}
      >
        <Entypo name="chevron-right" size={20} color="#8F9BB3" />
      </TouchableOpacity>
    </View>
  );
};

export default Pagination;
